import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "name",
    "description",
    "price",
    "pricePerSqft",
    "pricingModel",
    "recurring",
    "billingFrequency",
    "quoteRequest",
    "thumbnailPreset",
    "thumbnailFile",
    "previewName",
    "previewDescription",
    "previewPrice",
    "previewPriceLabel",
    "previewThumbnail",
    "previewBadges",
    "previewQuoteRequest"
  ]

  connect() {
    this.update()
  }

  update() {
    this.updateName()
    this.updateDescription()
    this.updatePrice()
    this.updateThumbnail()
    this.updateBadges()
  }

  updateName() {
    const name = this.nameTarget.value || "Service Name"
    this.previewNameTarget.textContent = name
  }

  updateDescription() {
    const description = this.descriptionTarget.value
    if (description) {
      this.previewDescriptionTarget.textContent = description
      this.previewDescriptionTarget.classList.remove("hidden")
    } else {
      this.previewDescriptionTarget.classList.add("hidden")
    }
  }

  updatePrice() {
    const isQuoteRequest = this.quoteRequestTarget.checked

    if (isQuoteRequest) {
      this.previewPriceTarget.classList.add("hidden")
      this.previewQuoteRequestTarget.classList.remove("hidden")
      return
    }

    this.previewPriceTarget.classList.remove("hidden")
    this.previewQuoteRequestTarget.classList.add("hidden")

    const pricingModel = this.pricingModelTarget.value
    let price = 0
    let label = ""

    if (pricingModel === "per_sqft") {
      price = parseFloat(this.pricePerSqftTarget.value) || 0
      label = "est."
    } else {
      price = parseFloat(this.priceTarget.value) || 0
      const isRecurring = this.recurringTarget.checked
      if (isRecurring) {
        const frequency = this.billingFrequencyTarget.value
        label = `/ ${this.frequencyLabel(frequency)}`
      }
    }

    this.previewPriceTarget.querySelector("[data-price]").textContent = this.formatCurrency(price)
    this.previewPriceLabelTarget.textContent = label
  }

  updateThumbnail() {
    const isQuoteRequest = this.quoteRequestTarget.checked
    const preset = this.thumbnailPresetTarget.value
    const hasFile = this.thumbnailFileTarget.files && this.thumbnailFileTarget.files.length > 0

    // Show quote request icon
    if (isQuoteRequest) {
      this.previewThumbnailTarget.innerHTML = `
        <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
          <svg class="size-10 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
          </svg>
        </div>
      `
      return
    }

    // Show file preview
    if (hasFile) {
      const file = this.thumbnailFileTarget.files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewThumbnailTarget.innerHTML = `
          <img src="${e.target.result}" class="absolute inset-0 w-full h-full object-cover" />
        `
      }
      reader.readAsDataURL(file)
      return
    }

    // Show preset image
    if (preset) {
      this.previewThumbnailTarget.innerHTML = `
        <img src="/service_presets/${preset}.jpg" class="absolute inset-0 w-full h-full object-cover" />
      `
      return
    }

    // Show placeholder (dark theme)
    this.previewThumbnailTarget.innerHTML = `
      <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
        <svg class="size-10 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>
      </div>
    `
  }

  updateBadges() {
    const isQuoteRequest = this.quoteRequestTarget.checked
    if (isQuoteRequest) {
      this.previewBadgesTarget.classList.add("hidden")
      return
    }

    this.previewBadgesTarget.classList.remove("hidden")

    const isRecurring = this.recurringTarget.checked
    const pricingModel = this.pricingModelTarget.value
    const isPerSqft = pricingModel === "per_sqft"

    let badges = ""
    if (isRecurring) {
      badges += '<span class="badge badge-blue text-xs">Recurring</span>'
    }
    if (isPerSqft) {
      badges += '<span class="badge badge-purple text-xs">Per Sqft</span>'
    }

    this.previewBadgesTarget.innerHTML = badges
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  frequencyLabel(frequency) {
    const labels = {
      weekly: "week",
      biweekly: "2 weeks",
      monthly: "month",
      quarterly: "quarter",
      annually: "year"
    }
    return labels[frequency] || frequency
  }
}
